// src/pages/Transaction.js

import React, {
  useMemo,
} from "react";

const ASSET_LABELS = {
  uswd: "USWD",
  ghsx: "GHSX",
  usdt: "USDT",
};

export default function Transaction({
  asset = "uswd",
  uswdHistory = [],
  ghsxHistory = [],
  usdtHistory = [],
  onBack,
  mobileView = false,
}) {

  const selectedAsset =
    asset === "ghsx" || asset === "usdt"
      ? asset
      : "uswd";

  const history = useMemo(() => {

    const source =
      selectedAsset === "uswd"
        ? uswdHistory
        : selectedAsset === "ghsx"
        ? ghsxHistory
        : usdtHistory;

    return [...source].sort((a, b) => {

      const aTime =
        new Date(
          a.timestamp ||
          a.createdAt
        ).getTime();

      const bTime =
        new Date(
          b.timestamp ||
          b.createdAt
        ).getTime();

      return bTime - aTime;

    });

  }, [
    selectedAsset,
    uswdHistory,
    ghsxHistory,
    usdtHistory,
  ]);

  return (

    <div className="flex h-full flex-col overflow-hidden">

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="border-b p-4"
        style={mobileView ? { paddingTop: "max(16px, env(safe-area-inset-top, 16px))" } : {}}
      >

        <div className="flex items-center gap-3">
{onBack && (

  <button
    onClick={onBack}
    aria-label="Back"
    className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full
               text-neutral-700 dark:text-neutral-200
               hover:bg-neutral-100 dark:hover:bg-neutral-800
               active:opacity-60 touch-manipulation transition-colors"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </svg>
  </button>

)}
         
          <h2 className="text-lg font-semibold">
            {ASSET_LABELS[selectedAsset]} Transactions
          </h2>

        </div>

      </div>

      {/* ======================================================
          HISTORY
      ====================================================== */}

           <div
        className="flex-1 overflow-y-auto"
      >

        {history.length === 0 ? (

          <div className="p-8 text-center text-neutral-500">
            No transactions found.
          </div>

        ) : (

          history.map((tx) => {

            const isUswdOrGhsx =
              selectedAsset === "uswd" ||
              selectedAsset === "ghsx";

            const isDeposit =
              isUswdOrGhsx
                ? tx.direction === "received"
                : tx.direction === "deposit";

            const txHash =
              isUswdOrGhsx
                ? tx.txHash
                : tx.txSig;

            const timestamp =
              tx.timestamp ||
              tx.createdAt;

            return (

              <div
                key={
                  tx._id ||
                  txHash
                }
                className="border-b p-4 dark:border-neutral-700"
              >

                <div className="flex items-center justify-between">

                  {/* LEFT */}

                  <div className="flex items-center gap-3">

                    <div
                      className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold
                      ${
                        isDeposit
                          ? "bg-green-100 text-green-600 dark:bg-green-900/30"
                          : "bg-red-100 text-red-600 dark:bg-red-900/30"
                      }`}
                    >
                      {isDeposit ? "↓" : "↑"}
                    </div>

                    <div>

                      <div
                        className={`text-sm font-medium
                        ${
                          isDeposit
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {isDeposit
                          ? "Received"
                          : "Sent"}
                      </div>

                      <div className="mt-0.5 text-xs text-neutral-400">
                        {new Date(
                          timestamp
                        ).toLocaleString()}
                      </div>

                    {!isUswdOrGhsx &&
                      txHash && (

                      <a
                        href={`https://solscan.io/tx/${txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 -ml-2 inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs text-blue-500
                                   hover:underline active:bg-blue-50 dark:active:bg-blue-900/20 touch-manipulation"
                      >
                        View on Solscan

                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>

                      </a>

                    )}

                    </div>

                  </div>

                  {/* RIGHT */}

                  <div
                    className={`text-base font-semibold
                    ${
                      isDeposit
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {isDeposit ? "+" : "-"}
                    {Number(
                      tx.amount || 0
                    ).toFixed(2)}
                    {" "}
                    {ASSET_LABELS[selectedAsset]}
                  </div>

                </div>

                {!isUswdOrGhsx &&
                  Number(
                    tx.committed || 0
                  ) > 0 && (

                  <div className="mt-2 pl-12 text-xs text-yellow-600">
                    Committed:{" "}
                    {Number(
                      tx.committed
                    ).toFixed(2)}
                    {" "}
                    USDT
                  </div>

                )}

               {isUswdOrGhsx && (

  <div className="mt-2 pl-12 text-xs text-neutral-500">

    {txHash && (

      <div className="break-all">

        <span className="font-medium">
          TX Hash:
        </span>
        {" "}
        <span className="font-mono">
          {txHash}
        </span>

      </div>

    )}

    {tx.status && (

      <div className="mt-1">

        <span className="font-medium">
          Status:
        </span>
        {" "}
        {tx.status}

      </div>

    )}

  </div>

)}

              </div>

            );

          })

        )}

      </div>

    </div>

  );

}